import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { ActivityApi, Activity, ActivityInstance } from '@api';
import { SupervisorContext } from './SupervisorContext';
import { isRoutine } from '@/utils/routine';

/**
 * RoutineScene — rutinerna (aktivitet + steg) för dagens och veckans instanser.
 *
 * Bakgrunden: dag- och veckovyerna hämtar INSTANSER (`getActivityInstances`), och en
 * instans bär bara namn, tid och `checkedSubtasks` — inte aktivitetens delsteg. För
 * att en rutinrad ska kunna visa "en sak i taget" ("Morgonrutin · Äta frukost")
 * behövs moderaktiviteten, och att hämta den per rad hade blivit en förfrågan per
 * aktivitet i listan.
 *
 * Därför EN förfrågan: `getAllActivities` ger användarens hela lista, och vi behåller
 * bara rutinerna i en uppslagning på activityId. Vanliga aktiviteter kastas direkt — de
 * behöver inget här, och listan ska inte växa till en andra kopia av kalendern.
 *
 * Just `getAllActivities` och inte sökningen `getActivitiesByParam('')`: den senare är
 * ett sökförslag som avdubblerar på namn och tar fyra träffar, så bara de fyra första
 * rutinnamnen hade hittats — resten hade tyst visats som vanliga aktiviteter.
 *
 * Läsningen följer handledarläget precis som CalendarContext: med vald brukare läses
 * brukarens rutiner, annars de egna.
 */

export type RoutineContextType = {
  /** Rutinen bakom en instans, eller undefined när instansen inte är en rutin. */
  getRoutine: (instance: ActivityInstance) => Activity | undefined;
  /** Läs om rutinerna (efter att en ny rutin skapats eller ändrats). */
  refreshRoutines: () => Promise<void>;
};

export const RoutineContext = createContext<RoutineContextType>({
  getRoutine: () => undefined,
  refreshRoutines: async () => {},
});

export const RoutineScene = ({ children }: { children?: React.ReactNode }) => {
  const { currentClient, isSupervising } = useContext(SupervisorContext);
  const [routines, setRoutines] = useState<Record<number, Activity>>({});

  const supervisedTag = currentClient?.tag;

  const refreshRoutines = useCallback(async () => {
    // Handledarläge utan vald brukare har ingen att läsa rutiner för; att fråga ändå
    // hade gett handledarens EGNA rutiner och visat fel steg på brukarens rader.
    if (isSupervising && !currentClient) {
      setRoutines({});
      return;
    }
    const activities = await new ActivityApi().getAllActivities({ supervisedTag });
    const found: Record<number, Activity> = {};
    (activities ?? [])
      .filter((activity) => isRoutine(activity))
      .forEach((activity) => {
        found[activity.activityId] = activity;
      });
    setRoutines(found);
  }, [isSupervising, currentClient, supervisedTag]);

  useEffect(() => {
    refreshRoutines();
  }, [refreshRoutines]);

  const getRoutine = useCallback(
    (instance: ActivityInstance) => routines[instance.parentActivityId],
    [routines],
  );

  return (
    <RoutineContext.Provider value={{ getRoutine, refreshRoutines }}>
      {children}
    </RoutineContext.Provider>
  );
};

/** Bekvämlighet för vyer som bara ska visa rätt etikett på en rad. */
export const useRoutines = (): RoutineContextType => useContext(RoutineContext);
